import { Controller, FormProvider, useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "@lib/dayjs";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";

import CustomDuration from "../components/CustomDuration";
import TimeSlotSelector from "../components/TimeSlotSelector";
import { ChevronDown } from "lucide-react";
import { meetingDurations } from "pages/CalendarsPage/meeting.constant";
import { useSnackbar } from "notistack";
import { useSearchParams } from "react-router-dom";
import PollInformationBanner from "../components/PollInformationBanner";
import { isObjectEmpty } from "utils/helperFunction";
import CustomWeekTimeCalendar from "@components/CustomWeekTimeCalendar/CustomWeekTimeCalendar";
import {
  durationType,
  ReviewFormData,
  TimeSlotRange,
} from "@pages/CalendarsPage/SchedulingSection/form.types";
import {
  sortAvailabilityGroup,
  sortAvailabilitySlots,
} from "@utils/meetingHelperFunction";

const timeSlotRangeSchema = z.object({
  startTime: z.string(),
  endTime: z.string(),
});

const meetingDurationSchema = z
  .object({
    label: z.string().nonempty("Please select a label"),
    type: z.nativeEnum(durationType),
    duration: z.number(),
  })
  .nullable() // allow null before selection
  .refine((val) => val !== null, {
    message: "Please select a meeting duration.",
  });

const formSchema = z.object({
  meetingDuration: meetingDurationSchema,
  availabilitySlots: z
    .record(z.string(), z.array(timeSlotRangeSchema))
    .refine((val) => Object.keys(val).length > 0, {
      message: "At least one availability slot is required",
    }),
});

type FormValues = z.infer<typeof formSchema>;

type MeetingDateSelectionSectionProps = {
  formData: ReviewFormData;
};

const MeetingDateSelectionSection = forwardRef(
  (props: MeetingDateSelectionSectionProps, ref) => {
    const { formData } = props;
    const methods = useForm<FormValues>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        meetingDuration: formData?.meetingDuration || meetingDurations[3],
        availabilitySlots: formData?.availabilitySlots || {},
      },
    });
    const meetingDurationFields = methods?.watch("meetingDuration");

    const { enqueueSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();
    const section = searchParams.get("section");
    const isMeetingPoll = searchParams.get("convertedInto") === "meetingPoll";
    const isMeetingInvite =
      searchParams.get("type") === "invite" && !isMeetingPoll;
    const addParam = (key: string, value: string, replace?: boolean) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(key, value);
          return next;
        },
        { replace }
      );
    };

    // Local state
    const [isCustomTimeSelectorOpen, setIsCustomTimeSelectorOpen] =
      useState(false);
    const [isDropdownOpen, setisDropdownOpen] = useState(false);
    const [showPollInfoBanner, setShowPollInfoBanner] = useState(false);

    const handleDateSelection = (value: {
      date: string;
      timeInMinutes: number;
      convert?: boolean;
    }) => {
      console.log("date selectoin", value);
      const selectedDate = dayjs(value?.date).format("YYYY-MM-DD");
      const meetingDuration = meetingDurationFields?.duration;
      const meetingDurationType = meetingDurationFields?.type;

      const availabiltySlotsFields = methods?.getValues("availabilitySlots");
      const dateExist = Object.entries(availabiltySlotsFields)?.some(
        ([key, _]) => key === selectedDate
      );

      const startTime = dayjs(value?.date)
        .set("minutes", value?.timeInMinutes)
        .toISOString();
      const endTime = dayjs(startTime)
        .add(
          meetingDuration,
          meetingDurationType === durationType.hour ? "hours" : "minutes"
        )
        .toISOString();
      const newTimeSlot: TimeSlotRange = {
        startTime,
        endTime,
      };

      // Conditions for specific time slot
      if (isMeetingInvite && !value?.convert) {
        if (isObjectEmpty(availabiltySlotsFields)) {
          setShowPollInfoBanner(true);
        }

        if (!dateExist) {
          methods?.setValue("availabilitySlots", {
            [selectedDate]: [newTimeSlot],
          });
        } else {
          methods?.setValue(`availabilitySlots.${selectedDate}`, [newTimeSlot]);
        }
        return;
      }

      // Conditions for general time slots
      const updatedAvailabilitySlots: Record<string, TimeSlotRange[]> = {
        ...availabiltySlotsFields,
      };

      if (!dateExist) {
        Object.assign(updatedAvailabilitySlots, {
          [selectedDate]: [newTimeSlot],
        });
        methods?.setValue(
          "availabilitySlots",
          sortAvailabilityGroup(updatedAvailabilitySlots)
        );
        console.log(
          "updatedAvailabilitySlots",
          sortAvailabilityGroup(updatedAvailabilitySlots)
        );
      } else {
        const slots =
          methods.getValues(`availabilitySlots.${selectedDate}`) || [];

        methods?.setValue(
          `availabilitySlots.${selectedDate}`,
          sortAvailabilitySlots([...slots, newTimeSlot])
        );
      }
    };

    // Handles meeting duration change
    const updatedSlots = useMemo(() => {
      const availabiltySlotsFields = methods?.getValues("availabilitySlots");
      if (!meetingDurationFields) return availabiltySlotsFields;

      const { duration, type } = meetingDurationFields;
      return Object.fromEntries(
        Object.entries(availabiltySlotsFields).map(([day, slots]) => [
          day,
          slots.map((slot) => ({
            startTime: slot.startTime,
            endTime: dayjs(slot.startTime)
              .add(duration, type === durationType.hour ? "hours" : "minutes")
              .toISOString(),
          })),
        ])
      );
    }, [meetingDurationFields]);

    const convertToMeetingPoll = () => {
      addParam("convertedInto", "meetingPoll", true);
      // setSearchParams(
      //   (prev) => {
      //     const next = new URLSearchParams(prev);
      //     next.set("type", "meetingPoll");
      //     return next;
      //   },
      //   { replace: true }
      // );
      setShowPollInfoBanner(false);
      handleDateSelection({
        date: dayjs().toISOString(),
        timeInMinutes: dayjs().minute(),
        convert: true,
      });
    };

    // Ref methods
    useImperativeHandle(ref, () => ({
      getData: () => methods?.getValues(),
      submit: () =>
        new Promise((resolve, reject) => {
          methods?.handleSubmit(
            (data) =>
              resolve({
                ...data,
                slotSelectionType: isMeetingInvite ? "specific" : "general",
              }),
            (errors) => reject(errors)
          )();
        }),
    }));

    useEffect(() => {
      const errorMessage =
        methods?.formState.errors?.availabilitySlots?.message;
      if (errorMessage) {
        enqueueSnackbar("At least one availability slot is required", {
          variant: "error",
        });
        // doSomethingOnError();
      }
    }, [methods?.formState.errors?.availabilitySlots]);

    return (
      <FormProvider {...methods}>
        <div className="flex gap-5 flex-1 min-h-0 pt-8">
          {/* Left Section */}
          <div className="flex flex-col flex-1 min-h-0">
            <Controller
              name="meetingDuration"
              control={methods?.control}
              rules={{
                required: "This field is required.",
              }}
              render={({ field, fieldState }) => {
                return (
                  <div className="flex flex-col">
                    <span className="font-montserrat text-charcoal font-medium text-xs mb-2">
                      Meeting duration
                    </span>
                    <DropdownMenu
                      open={isDropdownOpen}
                      onOpenChange={setisDropdownOpen}
                    >
                      <DropdownMenuTrigger
                        defaultValue={meetingDurations[0]?.duration}
                        className="flex justify-between font-montserrat text-left text-charcoal font-medium text-sm mb-2 border rounded-md py-2 px-3"
                      >
                        {field?.value?.label || "Select duration"}
                        <ChevronDown className="w-4 h-4" color="gray" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-white" align="start">
                        {meetingDurations?.map((durationOption) => {
                          return (
                            <DropdownMenuItem
                              key={durationOption?.duration}
                              className="text-xs outline-none hover:bg-gray-100 font-medium hover:cursor-pointer"
                              onSelect={() => {
                                console.log("durationOption", durationOption);
                                field.onChange(durationOption);
                                // methods?.setValue("specificDateTime", {
                                //   ...fields.specificDateTime,
                                //   endTime: dayjs(
                                //     fields?.specificDateTime?.startTime
                                //   ).add(durationOption?.duration, "hours"),
                                // });
                              }}
                            >
                              {durationOption?.label}
                            </DropdownMenuItem>
                          );
                        })}
                        <DropdownMenuItem
                          className="text-xs outline-none font-semibold bg-secondary text-white hover:cursor-pointer rounded-2xl"
                          onSelect={() => {
                            setisDropdownOpen(false);
                            setIsCustomTimeSelectorOpen(true);
                          }}
                        >
                          Custom Time +
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {fieldState.error && (
                      <span className="font-medium whitespace-pre text-xs text-red-500">
                        {fieldState.error?.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            {/* List of selected date */}
            <div
              className={`flex flex-col border-t ${
                isMeetingInvite ? "" : "flex-1"
              } min-h-0 border-gray-100 py-2.5 mt-3 overflow-y-scroll no-scrollbar`}
            >
              <span className="font-medium text-xs text-gray-400 text-center">
                Select the date and time from the calendar
              </span>
              <Controller
                name="availabilitySlots"
                control={methods.control}
                render={({ field, fieldState }) => {
                  return (
                    <div className="flex flex-col">
                      {Object.entries(field?.value)?.map(([date]) => {
                        return (
                          <TimeSlotSelector
                            date={date}
                            updatedSlots={updatedSlots[date]}
                            isMeetingInvite={isMeetingInvite}
                          />
                        );
                      })}
                    </div>
                  );
                }}
              />
            </div>

            <PollInformationBanner
              showPollInfoBanner={showPollInfoBanner}
              setShowPollInfoBanner={setShowPollInfoBanner}
              onTryNow={convertToMeetingPoll}
            />
          </div>
          {/* Right Section */}
          <div className="w-[55%] bg-black flex-col min-h-0">
            <CustomWeekTimeCalendar onPressDateSlot={handleDateSelection} />
          </div>

          {/* Custom Duration Dialog Box*/}
          <CustomDuration
            isCustomTimeSelectorOpen={isCustomTimeSelectorOpen}
            setIsCustomTimeSelectorOpen={setIsCustomTimeSelectorOpen}
            onSave={(formData) => {
              const meetingDuration = formData?.duration;
              const meetingDurationType = durationType[formData?.durationType];
              methods.setValue("meetingDuration", {
                label: `${meetingDuration} ${durationType[meetingDurationType]}`,
                duration: meetingDuration,
                type: meetingDurationType,
              });
            }}
          />
        </div>
      </FormProvider>
    );
  }
);

export default MeetingDateSelectionSection;
